import { watch, onMounted, onBeforeUnmount, nextTick, unref } from 'vue';
import { gsap, prefersReducedMotion } from '@/features/hr-workspace/utils/gsapOrg';
import {
  SIDEBAR_WIDTHS,
  SIDEBAR_MOTION
} from '@/features/hr-workspace/constants/sidebarLayout';

const FADE_SEL = [
  '.sidebar-brand__copy',
  '.sidebar-country-switcher__dropdown',
  '.sidebar-nav-link__text',
  '.sidebar-nav-badge',
  '.sidebar-user-card__copy',
  '.sidebar-user-card__menu-icon'
].join(', ');
const SECTION_LABEL_SEL = '.sidebar-nav-section__label';
const SECTION_FOLLOW_SEL = '.sidebar-nav-section + .sidebar-nav-section';
const LABELS_PENDING_CLASS = 'sidebar-root--labels-pending';
const MOTION_LOCK_CLASS = 'sidebar-root--motion-lock';
const HEADER_PROGRESS = '--sidebar-header-collapse';
const MOTION = SIDEBAR_MOTION;
const SECTION_LABEL_CLEAR =
  'height,maxHeight,margin,marginTop,marginBottom,padding,paddingTop,paddingBottom,overflow,opacity,visibility';
const SECTION_GAP_EXPANDED = '0.875rem';
const SECTION_GAP_RAIL = '0.5rem';

/**
 * Width and header geometry share one tween. Interrupting it preserves the painted
 * pose; a newer request also invalidates work waiting for Vue's next DOM update.
 * Rail CSS is applied only once all geometry has reached its collapsed endpoint.
 */
export function useSidebarCollapseMotion(sidebarRef, state) {
  let activeTl = null;
  let requestId = 0;
  let hasMounted = false;
  let disposed = false;

  const query = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);
  const fadeNodes = (root) => query(root, FADE_SEL);
  const sectionLabels = (root) => query(root, SECTION_LABEL_SEL);
  const sectionFollowers = (root) => query(root, SECTION_FOLLOW_SEL);
  const setRail = (on) => { state.isRailLayout.value = !!on; };
  const setLabelsPending = (el, on) => el.classList.toggle(LABELS_PENDING_CLASS, !!on);
  const setMotionLock = (el, on) => el.classList.toggle(MOTION_LOCK_CLASS, !!on);
  const resetFade = (nodes) => gsap.set(nodes, { clearProps: 'opacity,visibility' });

  const snapSectionChrome = (root, open) => {
    const labels = sectionLabels(root);
    const followers = sectionFollowers(root);
    if (open) {
      gsap.set(labels, { clearProps: SECTION_LABEL_CLEAR });
      gsap.set(followers, { clearProps: 'marginTop' });
    } else {
      gsap.set(labels, {
        height: 0, maxHeight: 0, marginTop: 0, marginBottom: 0,
        paddingTop: 0, paddingBottom: 0, overflow: 'hidden', autoAlpha: 0
      });
      gsap.set(followers, { marginTop: SECTION_GAP_RAIL });
    }
  };

  // Batch natural-height measurements to avoid a layout flush for every section.
  const measureLabels = (labels) => {
    const properties = ['height', 'maxHeight', 'visibility'];
    const saved = labels.map((label) => properties.map((key) => label.style[key]));
    labels.forEach((label) => {
      label.style.height = 'auto';
      label.style.maxHeight = 'none';
      label.style.visibility = 'hidden';
    });
    const heights = labels.map((label) => label.getBoundingClientRect().height);
    labels.forEach((label, index) => {
      properties.forEach((key, property) => { label.style[key] = saved[index][property]; });
    });
    return heights;
  };

  const killTl = () => {
    requestId += 1;
    activeTl?.kill();
    activeTl = null;
    // Do not clear styles here: they are the starting pose for a reversal.
  };

  const isCurrent = (id, el) => !disposed && id === requestId && el === sidebarRef.value;

  const snap = async (el, open, id, mobile = false) => {
    setRail(!open && !mobile);
    gsap.set(el, { [HEADER_PROGRESS]: open || mobile ? 0 : 1 });
    await nextTick();
    if (!isCurrent(id, el)) return;
    setLabelsPending(el, false);
    setMotionLock(el, false);
    gsap.set(el, mobile ? { clearProps: 'width' } : {
      width: open ? SIDEBAR_WIDTHS.expanded : SIDEBAR_WIDTHS.rail
    });
    if (open) resetFade(fadeNodes(el));
    else gsap.set(fadeNodes(el), { autoAlpha: 0 });
    snapSectionChrome(el, open);
  };

  const run = async (mode, opts = {}) => {
    const el = sidebarRef.value;
    if (!el || disposed) return;
    const open = mode === 'expand' || mode === 'snapExpanded';
    const wasMoving = !!activeTl;
    killTl();
    const id = requestId;

    if (unref(state.isMobile)) {
      await snap(el, true, id, true);
      return;
    }
    const animate = opts.animate !== false && !mode.startsWith('snap') && !prefersReducedMotion();
    if (!animate) {
      await snap(el, open, id);
      return;
    }

    // Freeze current geometry before removing rail CSS. A reversal keeps these
    // inline values, including opacity and section heights, from the old tween.
    const width = el.getBoundingClientRect().width;
    const progress = Number.parseFloat(gsap.getProperty(el, HEADER_PROGRESS));
    const labels = sectionLabels(el);
    const fromHeights = labels.map((label) => label.getBoundingClientRect().height);
    gsap.set(el, {
      width,
      [HEADER_PROGRESS]: Number.isFinite(progress) ? progress : (unref(state.isRailLayout) ? 1 : 0)
    });
    labels.forEach((label, index) => {
      gsap.set(label, { height: fromHeights[index], maxHeight: 'none', overflow: 'hidden' });
    });
    if (open && !wasMoving) gsap.set(fadeNodes(el), { autoAlpha: 0 });
    setLabelsPending(el, true);
    setMotionLock(el, true);
    setRail(false);
    await nextTick();
    if (!isCurrent(id, el)) return;

    const nodes = fadeNodes(el);
    const followers = sectionFollowers(el);
    const heights = open ? measureLabels(labels) : [];
    const timeline = gsap.timeline({
      paused: true,
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        if (!isCurrent(id, el)) return;
        activeTl = null;
        setRail(!open);
        setLabelsPending(el, false);
        setMotionLock(el, false);
        if (open) resetFade(nodes);
        snapSectionChrome(el, open);
      }
    });
    activeTl = timeline;

    timeline.to(el, {
      width: open ? SIDEBAR_WIDTHS.expanded : SIDEBAR_WIDTHS.rail,
      [HEADER_PROGRESS]: open ? 0 : 1,
      duration: MOTION.widthDuration,
      ease: MOTION.widthEase
    }, 0);
    labels.forEach((label, index) => {
      timeline.to(label, {
        height: open ? heights[index] : 0,
        marginTop: 0,
        marginBottom: open ? '0.375rem' : 0,
        paddingTop: 0,
        paddingBottom: 0,
        autoAlpha: open ? 1 : 0,
        duration: MOTION.widthDuration,
        ease: MOTION.widthEase
      }, 0);
    });
    timeline.to(followers, {
      marginTop: open ? SECTION_GAP_EXPANDED : SECTION_GAP_RAIL,
      duration: MOTION.widthDuration,
      ease: MOTION.widthEase
    }, 0);
    if (open) {
      timeline.add(() => {
        if (isCurrent(id, el)) setLabelsPending(el, false);
      }, MOTION.labelFadeAt);
    }
    timeline.to(nodes, {
      autoAlpha: open ? 1 : 0,
      duration: open ? MOTION.labelFade : MOTION.fadeOut,
      ease: open ? MOTION.labelFadeEase : MOTION.fadeOutEase
    }, open ? MOTION.labelFadeAt : 0);
    timeline.play();
  };

  onMounted(async () => {
    const el = sidebarRef.value;
    if (!el || disposed) return;
    killTl();
    const id = requestId;
    const mobile = unref(state.isMobile);
    const open = mobile || !unref(state.isCollapsed) || unref(state.isHoverExpanded);
    await snap(el, open, id, mobile);
    if (isCurrent(id, el)) hasMounted = true;
  });

  watch(
    [() => unref(state.isCollapsed), () => unref(state.isHoverExpanded), () => unref(state.isMobile)],
    async ([collapsed, hoverExpanded, mobile], prev) => {
      if (!hasMounted) return;
      const open = mobile || !collapsed || hoverExpanded;
      const wasOpen = !!prev?.[2] || !prev?.[0] || !!prev?.[1];
      if (mobile || prev?.[2]) {
        await run(open ? 'snapExpanded' : 'snapRail', { animate: false });
      } else if (open !== wasOpen) {
        await run(open ? 'expand' : 'collapse');
      }
      // Pinning a hover-expanded sidebar preserves its in-progress opening tween.
    }
  );

  onBeforeUnmount(() => {
    disposed = true;
    killTl();
    const el = sidebarRef.value;
    if (!el) return;
    setLabelsPending(el, false);
    setMotionLock(el, false);
    resetFade(fadeNodes(el));
    gsap.set(sectionLabels(el), { clearProps: SECTION_LABEL_CLEAR });
    gsap.set(sectionFollowers(el), { clearProps: 'marginTop' });
    gsap.set(el, { clearProps: `width,${HEADER_PROGRESS}` });
  });

  return { run, killTl };
}
